import { useState } from 'react'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { WizardFileList } from './WizardFileList.tsx'
import { WizardDropzone } from './WizardDropzone.tsx'
import { DiscoverySummaryView } from './DiscoverySummaryView.tsx'
import { WizardQuestions } from './WizardQuestions.tsx'
import { SheetPanel } from './SheetPanel.tsx'
import { RETRY_BUTTON } from './retryStyle.ts'
import { ResumableOrders } from './ResumeNotices.tsx'
import { DETECTION_FAILED_REJECTION } from '../../../Services/OrderWizard/dropPipeline.ts'
import { givenAnswers } from '../../../Services/OrderWizard/answers.ts'
import { fileHandle } from '../../../Services/OrderWizard/fileHandles.ts'

const DROP_LABEL = 'Drop FASTQ files, a sample sheet or both — you can add more later'

export const StartScreen = ({ draft }: { draft: DraftState }) => {
  const dispatch = useDispatch()
  const [describing, setDescribing] = useState(false)
  const hasFiles = draft.fileOrder.length > 0
  const retryable = draft.phase === 'error' && !!draft.settings && Object.keys(draft.groups).length > 0
  const unread = Object.values(draft.files)
    .filter((file) => file.rejection === DETECTION_FAILED_REJECTION && !!fileHandle(draft.id, file.key))
    .map((file) => file.key)
  return (
    <div className="space-y-4">
      <label className="block">
        <span className="text-sm font-medium text-slate-700">Order name</span>
        <input
          data-testid="wizard-order-name"
          type="text"
          className="mt-1 w-full rounded-lg border border-gray-400 px-4 py-1 focus:outline-primary"
          value={draft.name}
          onChange={(event) => dispatch(wizard.draftRenamed({ draftId: draft.id, name: event.target.value }))}
        />
      </label>
      {describing || draft.description !== undefined ? (
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Description</span>
          <textarea
            data-testid="wizard-order-description"
            rows={2}
            autoFocus={describing}
            className="mt-1 w-full rounded-lg border border-gray-400 px-4 py-1 text-sm focus:outline-primary"
            value={draft.description ?? ''}
            onChange={(event) => dispatch(wizard.draftDescribed({ draftId: draft.id, description: event.target.value }))}
          />
        </label>
      ) : (
        <button
          type="button"
          className="-mt-2 text-sm text-slate-500 hover:text-slate-800 hover:underline"
          data-testid="wizard-add-description"
          onClick={() => setDescribing(true)}
        >
          + Add description
        </button>
      )}
      <WizardDropzone draft={draft} label={DROP_LABEL} />
      {draft.phase === 'detecting' && (
        <p className="flex items-center gap-2 text-sm text-slate-600">
          <span className="loading loading-spinner loading-sm" /> Reading the first files…
        </p>
      )}
      {draft.phase === 'creating' && (
        <p className="flex items-center gap-2 text-sm text-slate-600">
          <span className="loading loading-spinner loading-sm" /> Creating the order…
        </p>
      )}
      {draft.error && (
        <div className="alert alert-error text-sm" role="alert">
          <span>{draft.error}</span>
          {retryable && (
            <button type="button" className={RETRY_BUTTON} onClick={() => dispatch(wizard.orderCreationRequested({ draftId: draft.id }))}>
              Try again
            </button>
          )}
          {!retryable && unread.length > 0 && (
            <button
              type="button"
              className={RETRY_BUTTON}
              data-testid="wizard-detection-retry"
              onClick={() => dispatch(wizard.filesDropped({ draftId: draft.id, keys: unread }))}
            >
              Try again
            </button>
          )}
        </div>
      )}
      {hasFiles &&
        draft.phase === 'start' &&
        !draft.error &&
        Object.keys(draft.groups).length === 0 &&
        draft.discovery &&
        !draft.questions.length && (
          <p className="text-sm text-warning">None of the dropped files can be uploaded yet — see the reasons below.</p>
        )}
      {draft.sheet && Object.keys(draft.groups).length === 0 && draft.phase === 'start' && (
        <p className="text-sm text-slate-600" data-testid="wizard-sheet-waiting">
          The sample sheet is kept. Drop the FASTQ files next — the order is created from them, and the sheet is matched as they arrive.
        </p>
      )}
      <WizardQuestions draft={draft} />
      {draft.discovery && (
        <DiscoverySummaryView
          discovery={draft.discovery}
          settings={draft.settings}
          answers={givenAnswers(draft)}
          onReopen={(kind) => dispatch(wizard.answerReopened({ draftId: draft.id, kind }))}
        />
      )}
      <SheetPanel draft={draft} />
      <WizardFileList draft={draft} />
      <ResumableOrders draft={draft} />
    </div>
  )
}
